import { useRouteError } from 'react-router-dom';
import { Button } from '@toptal/picasso';
import exclamation from '@/assets/icons/exclamation.svg';

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    (error instanceof Error && error.name === 'ChunkLoadError')
  );
}

/**
 * Catch-all `errorElement` for the router. When a route fails to load because
 * its chunk is stale after a deploy, we show a friendly "new version" card with
 * a reload action instead of react-router's raw "Unexpected Application Error!".
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const isStaleChunk = isChunkLoadError(error);

  const title = isStaleChunk ? 'A new version is available' : 'Something went wrong';
  const description = isStaleChunk
    ? 'This page was updated since you opened the app. Reload to get the latest version.'
    : 'An unexpected error occurred while loading this page. Please try reloading.';

  return (
    <div
      data-testid="route-error-boundary"
      className="mx-auto flex w-full max-w-sm flex-col items-center px-4 py-16 text-center"
    >
      <img src={exclamation} alt="" className="mb-6 h-16 w-16" />
      <h1 className="mb-3 text-lg font-semibold tracking-tight">{title}</h1>
      <p className="mb-8 text-sm text-muted-foreground">{description}</p>
      <Button variant="primary" size="large" onClick={() => window.location.reload()}>
        Reload page
      </Button>
    </div>
  );
}
